import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import api from "@/lib/api";

const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 disabled:opacity-40";

function useArchive(onDone) {
  const [busy, setBusy] = useState(false);
  const run = async (body, label) => {
    if (!window.confirm(`${label}? They'll be hidden from lead lists and reports. You can bring them back from Leads → Archived.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-leads/archive", body);
      toast.success(`${data.archived} test lead${data.archived === 1 ? "" : "s"} deleted`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete test leads");
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

function Row({ r, on, toggle }) {
  return (
    <tr className={`border-t border-zinc-200 ${on ? "bg-amber-50" : ""}`} data-testid={`test-leads-row-${r.id}`}>
      <td className="px-3 py-2"><input type="checkbox" checked={on} onChange={() => toggle(r.id)} className="w-4 h-4 accent-black" data-testid={`test-leads-select-${r.id}`} /></td>
      <td className="px-3 py-2 whitespace-nowrap"><Link className="underline" to={`/leads/${r.id}`}>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Link></td>
      <td className="px-3 py-2" data-testid={`test-leads-person-${r.id}`}>
        <div className="font-bold">{`${r.first_name || ""} ${r.last_name || ""}`.trim() || "—"}</div>
        <div className="text-[10px] text-zinc-600">{[r.email, r.phone].filter(Boolean).join(" · ")}</div>
      </td>
      <td className="px-3 py-2">{r.firm_name || "—"}</td>
      <td className="px-3 py-2 font-mono">{r.page || "—"}</td>
      <td className="px-3 py-2">{r.state || "—"} · {r.accident_type || "—"}</td>
      <td className={`px-3 py-2 ${r.delivery_status === "delivered" ? "text-emerald-800" : "text-amber-800"}`}>{r.delivery_status || r.status || "—"}</td>
      <td className="px-3 py-2">{r.sent_by || "—"}</td>
    </tr>
  );
}

/** Recent test leads with tick boxes: delete the picked ones, or every test lead. Deleting hides (archives) them. */
export function TestLeadsTable({ rows, total, searching, onChanged }) {
  const [picked, setPicked] = useState(new Set());
  const { busy, run } = useArchive(() => { setPicked(new Set()); onChanged(); });
  useEffect(() => {
    setPicked((p) => new Set([...p].filter((id) => rows.some((r) => r.id === id))));
  }, [rows]);
  const empty = <div className="label-mono py-6 text-center" data-testid="test-leads-recent-empty">{searching ? "No test leads match your search" : "No test leads"}</div>;
  if (!rows.length && !(searching && total)) return empty;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = rows.length > 0 && picked.size === rows.length;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`${BTN} bg-white hover:bg-black hover:text-white`} disabled={!picked.size || busy}
                onClick={() => run({ ids: [...picked] }, `Delete ${picked.size} selected test lead${picked.size === 1 ? "" : "s"}`)} data-testid="test-leads-delete-selected">
          <Trash size={11} weight="bold" /> Delete selected{picked.size ? ` (${picked.size})` : ""}
        </button>
        <button type="button" className={`${BTN} border-red-700 text-red-800 bg-white hover:bg-red-700 hover:text-white`} disabled={busy}
                onClick={() => run({ all: true }, `Delete all ${total} test leads`)} data-testid="test-leads-delete-all">
          <Trash size={11} weight="bold" /> Delete all test leads ({total})
        </button>
        {!searching && total > rows.length && <span className="label-mono text-zinc-500" data-testid="test-leads-showing">Showing the latest {rows.length} of {total}</span>}
      </div>
      {!rows.length ? empty : <div className="overflow-x-auto border-2 border-black bg-white">
        <table className="w-full text-xs" data-testid="test-leads-recent">
          <thead className="bg-zinc-100 text-left uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2"><input type="checkbox" checked={allOn} onChange={() => setPicked(allOn ? new Set() : new Set(rows.map((r) => r.id)))} className="w-4 h-4 accent-black" data-testid="test-leads-select-all" aria-label="Select all" /></th>
              <th className="px-3 py-2">When</th><th className="px-3 py-2">Person</th><th className="px-3 py-2">Buyer</th><th className="px-3 py-2">Page</th><th className="px-3 py-2">State · case</th><th className="px-3 py-2">Delivery</th><th className="px-3 py-2">Sent by</th>
            </tr>
          </thead>
          <tbody>{rows.map((r) => <Row key={r.id} r={r} on={picked.has(r.id)} toggle={toggle} />)}</tbody>
        </table>
      </div>}
    </div>
  );
}
